import { AlertTriangle, Clock3 } from "lucide-react";
import { classifyRepeatDoses } from "@/domain/doseOverlapClassifier";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

function tone(kind: string) {
  if (kind === "near-duplicate" || kind === "overlap-likely") return "border-warning/50 bg-warning/5";
  if (kind === "extension-likely") return "border-primary/30 bg-primary/5";
  return "border-border bg-card";
}

function formatInterval(hours: number) {
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  return `${hours.toFixed(hours < 10 ? 1 : 0)} h`;
}

export function RepeatDoseAnalysisPanel({ doses = [] }: { doses?: any[] }) {
  const signals = classifyRepeatDoses(doses);
  const clinicallyRelevant = signals.filter((signal) => signal.kind !== "separate-episode");

  return (
    <Card className="min-w-0 space-y-3 overflow-hidden p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-medium">
            <Clock3 className="h-4 w-4 text-primary" /> Redose, sobreposição e prolongamento
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Classificação temporal farmacológica. Não presume potencialização, causalidade, abuso ou tolerância sem contexto e resposta observada.
          </p>
        </div>
        <Badge variant="outline" className="shrink-0">{clinicallyRelevant.length} sinal(is)</Badge>
      </div>

      {clinicallyRelevant.length ? (
        <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
          {clinicallyRelevant.slice(0, 12).map((signal) => (
            <div key={signal.id} className={`min-w-0 space-y-2 rounded-xl border p-3 ${tone(signal.kind)}`}>
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="break-words text-sm font-medium">{signal.substanceLabel}: {signal.label}</div>
                  <div className="text-xs text-muted-foreground">intervalo {formatInterval(signal.intervalHours)} · confiança {signal.confidence}</div>
                </div>
                <Badge variant="secondary" className="shrink-0 text-[10px]">{signal.kind}</Badge>
              </div>
              <p className="text-xs leading-relaxed">{signal.explanation}</p>
              <div className="text-xs">
                <span className="font-medium">Pode resultar em:</span> {signal.possibleConsequences.join("; ")}.
              </div>
              {signal.missing.length ? (
                <div className="flex items-start gap-1.5 text-[11px] text-warning">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>Para aumentar a confiança, falta: {signal.missing.join("; ")}.</span>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          Nenhuma repetição temporalmente relevante foi detectada no período. Isso não exclui carry-over individual ou registros ausentes.
        </div>
      )}
    </Card>
  );
}
